import React, { useState, useEffect } from 'react';
import { Layers } from 'lucide-react';
import { useApiCaller } from '../utils/ApiUtilities';
import { getAdminModelTestListUrl, getAdminQuizSplitModelTestUrl } from '../utils/UrlUtilities';
import { constructSplitModelTestPayload } from '../utils/PayloadUtils';
import { SUBJECTS_BY_LEVEL, microTestLabel } from '../utils/Constants';
import { Checkbox } from './ui/checkbox';
import CloseButton from './ui/CloseButton';

const PURPLE_GRADIENT = 'linear-gradient(145deg,#7c3aed,#5b21b6)';

// Splits published TOEFL model tests into per-section micro quizzes (unpublished).
// On success it closes and hands the report to the parent via onGenerated(report).
const GenerateMicroQuizModal = ({ level = 'TOEFL', onClose, onGenerated }) => {
    const CallApi = useApiCaller();
    const allSections = SUBJECTS_BY_LEVEL[level] || ['Reading', 'Writing', 'Listening', 'Speaking'];

    const [modelTests, setModelTests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [selectedQuizIds, setSelectedQuizIds] = useState([]);
    const [selectedSections, setSelectedSections] = useState(allSections);
    const [generating, setGenerating] = useState(false);

    // Lock background scroll while the modal is open.
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => { document.body.style.overflow = prev; };
    }, []);

    useEffect(() => {
        const fetchModelTests = async () => {
            try {
                setLoading(true);
                const res = await CallApi({ url: getAdminModelTestListUrl(level), method: 'GET', token: true });
                if (res.success) {
                    setModelTests(res.data.items || []);
                } else {
                    setError(res.message || 'Failed to load model tests.');
                }
            } catch (err) {
                console.error('Error loading model tests:', err);
                setError('An error occurred while loading model tests.');
            } finally {
                setLoading(false);
            }
        };
        fetchModelTests();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [level]);

    const addQuiz = (quizId) => {
        const id = Number(quizId);
        if (!id || selectedQuizIds.includes(id)) return;
        setSelectedQuizIds(prev => [...prev, id]);
    };
    const removeQuiz = (quizId) => {
        setSelectedQuizIds(prev => prev.filter(id => id !== quizId));
    };
    const quizById = (id) => modelTests.find(mt => mt.quiz_id === id);

    const toggleSection = (section) => {
        setSelectedSections(prev => prev.includes(section) ? prev.filter(s => s !== section) : [...prev, section]);
    };

    const canGenerate = selectedQuizIds.length > 0 && selectedSections.length > 0 && !generating;

    const handleGenerate = async () => {
        setError('');
        setGenerating(true);
        try {
            const payload = constructSplitModelTestPayload(selectedQuizIds, selectedSections);
            const res = await CallApi({ url: getAdminQuizSplitModelTestUrl(), method: 'POST', payload, token: true });
            if (res.success) {
                // Hand the report to the parent, which closes this modal and shows the report modal.
                onGenerated(res.data);
            } else {
                setError(res.message || 'Failed to generate micro tests.');
            }
        } catch (err) {
            console.error('Error generating micro quizzes:', err);
            setError('An error occurred while generating micro tests.');
        } finally {
            setGenerating(false);
        }
    };

    return (
        <div style={{
            position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100, padding: '1rem',
        }}>
            <div style={{
                backgroundColor: '#ffffff', borderRadius: '0.9rem',
                boxShadow: '0 10px 25px -5px rgba(0,0,0,0.3)', width: '90%', maxWidth: 'min(680px, 96vw)',
                maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden',
            }}>
                {/* Header */}
                <div style={{ background: PURPLE_GRADIENT, color: '#fff', padding: '1.1rem 1.25rem', position: 'relative', flexShrink: 0 }}>
                    <CloseButton onClick={onClose} />
                    <h2 style={{ fontSize: '1.3rem', fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <Layers style={{ width: '1.25rem', height: '1.25rem' }} /> Generate {microTestLabel(level)} — {level}
                    </h2>
                    <p style={{ margin: '0.35rem 0 0', opacity: 0.85, fontSize: '0.85rem' }}>
                        Split a model test into per-section tests. New tests are created unpublished.
                    </p>
                </div>

                {/* Body (scrolls) */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
                    {loading && <p className="mb-0">Loading model tests…</p>}
                    {error && <p style={{ color: '#dc2626' }}>{error}</p>}

                    {!loading && !error && modelTests.length === 0 && (
                        <p className="text-muted mb-0">No published {level} model tests found.</p>
                    )}

                    {!loading && modelTests.length > 0 && (
                        <>
                            <h6 className="fw-bold mb-2">Select model test(s) to split</h6>
                            {/* Selected quizzes appear as removable pills (like the Question form's topic picker). */}
                            <div className="d-flex flex-wrap gap-2 mb-2">
                                {selectedQuizIds.map(id => {
                                    const mt = quizById(id);
                                    return (
                                        <span key={id} className="badge bg-info-subtle text-info d-flex align-items-center rounded-pill px-2 py-1">
                                            {mt ? mt.title : `#${id}`}
                                            <button type="button" className="btn-close ms-1 text-danger" aria-label="Remove" onClick={() => removeQuiz(id)}></button>
                                        </span>
                                    );
                                })}
                            </div>
                            <select
                                className="form-select mb-3"
                                value=""
                                onChange={e => { addQuiz(e.target.value); e.target.value = ''; }}
                            >
                                <option value="">Add a model test…</option>
                                {modelTests
                                    .filter(mt => !selectedQuizIds.includes(mt.quiz_id))
                                    .map(mt => (
                                        <option key={mt.quiz_id} value={mt.quiz_id}>
                                            {mt.title}
                                        </option>
                                    ))}
                            </select>

                            <h6 className="fw-bold mb-2">Sections to extract</h6>
                            <div className="d-flex flex-wrap gap-3" style={{ marginBottom: '0.5rem' }}>
                                {allSections.map(section => (
                                    <label key={section} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                                        <Checkbox checked={selectedSections.includes(section)} onCheckedChange={() => toggleSection(section)} />
                                        <span>{section}</span>
                                    </label>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{ borderTop: '1px solid #e5e7eb', padding: '0.85rem 1.25rem', display: 'flex', justifyContent: 'flex-end', gap: '0.6rem', flexShrink: 0 }}>
                    <button className="btn btn-light" onClick={onClose}>Close</button>
                    <button className="btn btn-primary" disabled={!canGenerate} onClick={handleGenerate}>
                        {generating ? 'Generating…' : `Generate ${microTestLabel(level)}`}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default GenerateMicroQuizModal;
